import { useState } from 'react';
import { STAMPS, squareName, type CardSpec, type StampId } from '../../engine';
import { OmenRune } from './night/OmenRune';
import {
  BARGAIN_PAWNS,
  debtLimit,
  type BargainId,
  type GainTarget,
  type NightRun,
  type Offer,
  type Profile,
} from '../../campaign';
import { t } from '../../i18n';
import { faceLong, itemName, itemText } from '../../app/describe';
import { CardView } from './CardView';
import { Modal } from './Modal';

/**
 * Choices some Long Night offers need (docs/SHOP_DESIGN.md §7–§13): a pawn to carve, a square for
 * an Omen, cards from the run deck, a Stamp, and the pawns a Bargain takes.
 */

export const pawnLabel = (p: Profile, run: NightRun, id: string): string => {
  const standIn = run.roster.find((r) => r.id === id)?.standIn;
  return standIn ? `${t('campaign.standIn')} ${id}` : (p.pawns[id]?.name ?? id);
};

export const cardName = (c: CardSpec): string => {
  const base = c.kind === 'wild' ? t('card.wild') : faceLong(c.face);
  const extras = [
    c.stamp ? itemName(c.stamp) : '',
    c.finish ? itemName(c.finish) : '',
    c.iou ? t('night.iou') : '',
  ].filter(Boolean);
  return extras.length ? `${base} (${extras.join(', ')})` : base;
};

/** An offer's name and what it does. */
export function offerName(o: Offer): string {
  switch (o.kind) {
    case 'superstition':
      return o.finish ? `${itemName(o.id)} (${itemName(o.finish)})` : itemName(o.id);
    case 'curio':
    case 'carving':
      return itemName(o.id);
    case 'appraisal':
      return itemName(`appraisal.${o.id}`);
    case 'card':
      return cardName(o.card);
    case 'bargain':
      return itemName(`bargain.${o.id}`);
    case 'route':
      return t('night.offer.route', { name: o.id });
    case 'omen':
      return itemName(`omen.${o.id}`);
  }
}

export function offerText(o: Offer): string {
  switch (o.kind) {
    case 'superstition': {
      const parts = [itemText(o.id)];
      if (o.finish) parts.push(itemText(o.finish));
      if (o.loan) parts.push(t('night.loan'));
      return parts.filter(Boolean).join(' ');
    }
    case 'curio':
    case 'carving':
      return itemText(o.id);
    case 'appraisal':
      return itemText(`appraisal.${o.id}`);
    case 'card':
      return [
        o.card.stamp ? itemText(o.card.stamp) : '',
        o.card.finish ? itemText(o.card.finish) : '',
      ]
        .filter(Boolean)
        .join(' ');
    case 'bargain':
      return itemText(`bargain.${o.id}`);
    case 'route':
      return t('night.offer.routeText');
    case 'omen':
      return `${itemText(`omen.${o.id}`)} ${t('night.offer.omenPlace')}`;
  }
}

/** Whether brass covers a price, debt allowed. */
export const affords = (run: NightRun, price: number): boolean =>
  run.brass - price >= -debtLimit(run);

/** What an offer needs chosen before it can be taken. */
export type TargetNeed =
  'pawn' | 'square' | 'item' | 'graft' | 'fusion' | 'card' | 'lostPawn' | null;

export function targetNeed(o: Offer): TargetNeed {
  if (o.kind === 'carving') return 'pawn';
  if (o.kind === 'omen') return 'square';
  if (o.kind !== 'bargain') return null;
  switch (o.id as BargainId) {
    case 'effigy':
      return 'item';
    case 'graft':
      return 'graft';
    case 'fusion':
      return 'fusion';
    case 'changeling':
      return 'card';
    case 'exhumation':
      return 'lostPawn';
    default:
      return null;
  }
}

/**
 * Asks what an offer needs (a pawn, a square, a card…) and, for a Bargain bought with pawns,
 * which pawns pay. Calls `onDone` with the choice.
 */
export function TargetPicker({
  profile,
  run,
  offer,
  payPawns = 0,
  onDone,
  onCancel,
}: {
  profile: Profile;
  run: NightRun;
  offer: Offer;
  /** Named pawns the deal takes as payment (the Back Room, the Séance). */
  payPawns?: number;
  onDone: (target: GainTarget, payWith: string[]) => void;
  onCancel: () => void;
}) {
  const need = targetNeed(offer);
  const [pawnId, setPawnId] = useState('');
  const [pawn2, setPawn2] = useState('');
  const [carving, setCarving] = useState('');
  const [square, setSquare] = useState<{ x: number; y: number } | null>(null);
  const [uid, setUid] = useState('');
  const [deckIndex, setDeckIndex] = useState<number | null>(null);
  const [payWith, setPayWith] = useState<string[]>([]);
  const named = run.roster.filter((r) => !r.standIn).map((r) => r.id);
  const lost = Object.keys(run.bestInscriptions).filter(
    (id) => !run.roster.some((r) => r.id === id),
  );
  const target: GainTarget = {
    ...(pawnId ? { pawnId } : {}),
    ...(pawn2 ? { pawn2 } : {}),
    ...(carving ? { carving } : {}),
    ...(square ? { square } : {}),
    ...(uid ? { uid } : {}),
    ...(deckIndex !== null ? { deckIndex } : {}),
  };
  const ready =
    payWith.length === payPawns &&
    (need === null ||
      (need === 'pawn' && !!pawnId) ||
      (need === 'lostPawn' && !!pawnId) ||
      (need === 'square' && !!square) ||
      (need === 'item' && !!uid) ||
      (need === 'card' && deckIndex !== null) ||
      (need === 'fusion' && !!pawnId && !!pawn2 && pawnId !== pawn2) ||
      (need === 'graft' && !!pawnId && !!pawn2 && !!carving && pawnId !== pawn2));
  const pawnSelect = (value: string, set: (v: string) => void, ids: readonly string[]) => (
    <select value={value} onChange={(e) => set(e.target.value)}>
      <option value="">—</option>
      {ids.map((id) => (
        <option key={id} value={id}>
          {pawnLabel(profile, run, id)}
          {(run.carvings[id] ?? []).length
            ? ` · ${(run.carvings[id] ?? []).map((c) => itemName(c.id)).join(', ')}`
            : ''}
        </option>
      ))}
    </select>
  );
  return (
    <Modal
      title={offerName(offer)}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn" onClick={onCancel}>
            {t('action.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!ready}
            onClick={() => onDone(target, payWith)}
          >
            {t('action.confirm')}
          </button>
        </>
      }
    >
      <p className="dim">{offerText(offer)}</p>
      {need === 'pawn' && (
        <label className="field">
          <span className="field-label">{t('night.target.carve')}</span>
          {pawnSelect(
            pawnId,
            setPawnId,
            run.roster.map((r) => r.id),
          )}
        </label>
      )}
      {need === 'lostPawn' && (
        <label className="field">
          <span className="field-label">{t('night.target.lostPawn')}</span>
          {lost.length === 0 ? (
            <span className="dim">{t('night.target.noneLost')}</span>
          ) : (
            pawnSelect(pawnId, setPawnId, lost)
          )}
        </label>
      )}
      {need === 'fusion' && (
        <>
          <label className="field">
            <span className="field-label">{t('night.target.keep')}</span>
            {pawnSelect(pawnId, setPawnId, named)}
          </label>
          <label className="field">
            <span className="field-label">{t('night.target.gone')}</span>
            {pawnSelect(
              pawn2,
              setPawn2,
              named.filter((id) => id !== pawnId),
            )}
          </label>
        </>
      )}
      {need === 'graft' && (
        <>
          <label className="field">
            <span className="field-label">{t('night.target.donor')}</span>
            {pawnSelect(
              pawnId,
              (v) => {
                setPawnId(v);
                setCarving('');
              },
              named.filter((id) => (run.carvings[id] ?? []).length > 0),
            )}
          </label>
          {pawnId && (
            <label className="field">
              <span className="field-label">{t('night.target.carving')}</span>
              <select value={carving} onChange={(e) => setCarving(e.target.value)}>
                <option value="">—</option>
                {(run.carvings[pawnId] ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {itemName(c.id)}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="field">
            <span className="field-label">{t('night.target.receiver')}</span>
            {pawnSelect(
              pawn2,
              setPawn2,
              run.roster.map((r) => r.id).filter((id) => id !== pawnId),
            )}
          </label>
        </>
      )}
      {need === 'item' && (
        <label className="field">
          <span className="field-label">{t('night.target.item')}</span>
          <select value={uid} onChange={(e) => setUid(e.target.value)}>
            <option value="">—</option>
            {run.mantel.map((i) => (
              <option key={i.uid} value={i.uid}>
                {itemName(i.id)}
              </option>
            ))}
          </select>
        </label>
      )}
      {need === 'card' && (
        <DeckPicker
          deck={run.deck}
          max={1}
          selected={deckIndex === null ? [] : [deckIndex]}
          onChange={(sel) => setDeckIndex(sel[0] ?? null)}
          filter={(c) => c.kind === 'normal'}
        />
      )}
      {need === 'square' && (
        // The next table's middle rows as a small board: the rune hovers over it until a square
        // is chosen, then settles there and etches itself in (§12.9 of the art direction).
        <div className="omen-board" role="group" aria-label={t('night.target.square')}>
          <p className="field-label">{t('night.target.square')}</p>
          {offer.kind === 'omen' && (
            <span className={`omen-hover ${square ? 'is-set' : ''}`} aria-hidden="true">
              <OmenRune kind={offer.id} />
            </span>
          )}
          <div className="omen-grid">
            {[4, 3, 2, 1].map((y) =>
              [0, 1, 2, 3, 4, 5].map((x) => {
                const taken = run.pendingOmens.some((o) => o.square.x === x && o.square.y === y);
                const on = square?.x === x && square.y === y;
                return (
                  <button
                    key={`${x}-${y}`}
                    type="button"
                    className={`omen-cell ${(x + y) % 2 ? 'is-light' : 'is-dark'} ${on ? 'is-on' : ''}`}
                    disabled={taken}
                    aria-pressed={on}
                    aria-label={squareName({ x, y })}
                    onClick={() => setSquare({ x, y })}
                  >
                    {on && offer.kind === 'omen' ? (
                      <OmenRune kind={offer.id} />
                    ) : (
                      <span className="omen-cell-name">{squareName({ x, y })}</span>
                    )}
                  </button>
                );
              }),
            )}
          </div>
        </div>
      )}
      {payPawns > 0 && (
        <fieldset className="field pay-pawns">
          <legend className="field-label">{t('night.target.payWith', { n: payPawns })}</legend>
          {named
            .filter((id) => id !== pawnId && id !== pawn2)
            .map((id) => (
              <label key={id} className="pawn-pick">
                <input
                  type="checkbox"
                  checked={payWith.includes(id)}
                  onChange={() =>
                    setPayWith((cur) =>
                      cur.includes(id)
                        ? cur.filter((x) => x !== id)
                        : cur.length < payPawns
                          ? [...cur, id]
                          : cur,
                    )
                  }
                />
                {pawnLabel(profile, run, id)}
              </label>
            ))}
          {run.roster.length - payPawns < 6 && <p className="warn">{t('night.target.keepSix')}</p>}
        </fieldset>
      )}
    </Modal>
  );
}

/** Pawns a Bargain takes as payment at the Back Room (Graft and Fusion take theirs in the deal). */
export const bargainPawns = (id: BargainId): number =>
  id === 'graft' || id === 'fusion' ? 0 : BARGAIN_PAWNS[id];

/** The run deck, to choose cards from (melting, Bench Curios, Detours). */
export function DeckPicker({
  deck,
  max,
  selected,
  onChange,
  filter,
  burning,
}: {
  deck: readonly CardSpec[];
  max: number;
  selected: readonly number[];
  onChange: (sel: number[]) => void;
  filter?: (c: CardSpec) => boolean;
  /** Cards being melted: an ember line runs round each and burns it inward. */
  burning?: readonly number[];
}) {
  return (
    <ul className="deck-picker" aria-label={t('night.deck', { n: deck.length })}>
      {deck.map((c, i) => {
        const on = selected.includes(i);
        const ok = !filter || filter(c);
        return (
          <li key={i} className={burning?.includes(i) ? 'is-burning' : undefined}>
            <CardView
              size="sm"
              face={c.kind === 'normal' ? c.face : null}
              wild={c.kind === 'wild'}
              stamp={c.stamp}
              stampValue={c.value}
              finish={c.finish}
              trick={c.trick}
              iou={c.iou}
              label={cardName(c)}
              state={on ? 'selected' : 'neutral'}
              pressed={on}
              disabled={!ok}
              onClick={() =>
                onChange(
                  on
                    ? selected.filter((x) => x !== i)
                    : max === 1
                      ? [i]
                      : selected.length < max
                        ? [...selected, i]
                        : [...selected],
                )
              }
            />
          </li>
        );
      })}
    </ul>
  );
}

/** A Stamp to choose (the Furnace). */
export function StampSelect({
  value,
  onChange,
}: {
  value: StampId | '';
  onChange: (s: StampId | '') => void;
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value as StampId | '')}>
      <option value="">—</option>
      {STAMPS.map((s) => (
        <option key={s} value={s}>
          {itemName(s)} — {itemText(s)}
        </option>
      ))}
    </select>
  );
}
